import { useCallback, useEffect, useRef, useState } from 'react';

import { BodyMicro } from '#/components/Text/BodyMicro';
import { H4 } from '#/components/Text/H4';
import { useLatest } from '#/hooks/useLatest';
import { useTransitionGroup } from '#/hooks/useTransitionGroup';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { TickElement } from '#/modules/salaries/components/Axis/Axis';
import { Axis } from '#/modules/salaries/components/Axis/Axis';
import { Line } from '#/modules/salaries/components/Line/Line';
import { normalizeDataToPixel } from '#/modules/salaries/helpers/normalizeDataToPixel';
import type { CareerPathAnalyticsData } from '#/modules/salaries/types/CareerPathAnalyticsData';
import type { CareerPathChartData } from '#/modules/salaries/types/CareerPathChartData';
import type { RidgeLineData } from '#/modules/salaries/types/RidgeLineData';
import { colors } from '#/theme/theme';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';

import {
  BarContainer,
  CareerPathChartContainer,
  CareerPathChartWrapper,
  ChartDataContainer,
  ChartDataWrapper,
  HorizontalAxisContainer,
  StyledBar,
  TooltipContainer,
} from './CareerPathChart.styled';
import { ModalButton } from './ModalButton';

function formatSalary(salary: number, locale: I18nLocale): string {
  return salary.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

type ChartProps = {
  analyticsData: CareerPathAnalyticsData;
  data: CareerPathChartData;
  loading: boolean;
};

export function CareerPathChart({ analyticsData, data, loading }: ChartProps) {
  const { i18n, locale } = useI18n();

  const [chartWidth, setChartWidth] = useState<number>(0);

  const { ridgeLineChart } = data || {};

  const chartRef = useRef<HTMLDivElement>(null);

  const analyticsDataRef = useLatest(analyticsData);

  useEffect(() => {
    const handleResize = () =>
      setChartWidth(chartRef?.current?.getBoundingClientRect().width ?? 0);

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [setChartWidth]);

  useElementReachedEffect({
    ref: chartRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Career Path Report Displayed', {
        ...analyticsDataRef.current,
        job_title_main: analyticsData.job_title_main,
        city: analyticsData.city,
        years_of_experience: analyticsData.years_of_experience,
      });
    }, [
      analyticsDataRef,
      analyticsData.job_title_main,
      analyticsData.city,
      analyticsData.years_of_experience,
    ]),
  });

  const {
    xAxisTicks,
    xGridLines,
    yAxisLabel,
    yGridLines,
    xRangeMin,
    xRangeMax,
  } = ridgeLineChart;

  const plotHeight = 50;
  const numberOfPlots = ridgeLineChart.data.length;
  const chartHeight = plotHeight * numberOfPlots;

  const duration = 500;
  const chartRange = xRangeMax - xRangeMin;

  const transitionFn = useTransitionGroup<RidgeLineData>(
    ridgeLineChart.data,
    duration,
  );

  const yTickComponents = transitionFn<TickElement>((item) => ({
    name: item.name,
    component: <ModalButton item={item} />,
  }));

  return (
    <CareerPathChartContainer data-qa-id="career-path-chart">
      <CareerPathChartWrapper>
        <Axis
          alignToGrid={false}
          direction="y"
          type="category"
          gridLines={yGridLines}
          tickSpace={10}
          chartWidth={chartHeight}
          animationStages={Object.fromEntries(
            transitionFn<[string, string]>((item, stage) => [item.name, stage]),
          )}
          duration={500}
          ticks={yTickComponents}
          label={yAxisLabel}
        />
        <ChartDataContainer
          ref={chartRef}
          $chartHeight={chartHeight}
          $loading={loading}
        >
          <ChartDataWrapper
            $xGridInterval={chartWidth / (xGridLines - 1) - 1}
            $yGridInterval={chartHeight / yGridLines}
            $showXGridLines
          >
            {chartWidth
              ? transitionFn((salary, stage, index) => {
                  if (salary && salary.count > 0) {
                    const lineWidth = chartWidth * (salary.count / chartRange);

                    const lowerBound68Coord = normalizeDataToPixel(
                      salary.sectionLimits.middle68LowerBound,
                      salary.xRangeMin,
                      salary.xRangeMax,
                      lineWidth,
                    );

                    const upperBound68Coord = normalizeDataToPixel(
                      salary.sectionLimits.middle68UpperBound,
                      salary.xRangeMin,
                      salary.xRangeMax,
                      lineWidth,
                    );

                    const hoverSections = {
                      [`interval68-${index}`]: {
                        coords: `${lowerBound68Coord} 0, ${lowerBound68Coord} 200, ${upperBound68Coord} 200, ${upperBound68Coord} 0`,
                      },
                    };

                    return (
                      <BarContainer
                        data-qa-id="career-path-chart-job"
                        key={index * ridgeLineChart.data.length}
                        $plotHeight={plotHeight}
                      >
                        <StyledBar
                          $animationDelay={0}
                          $active
                          $loading={false}
                          $duration={duration}
                          $stage={stage}
                          component={
                            <Line
                              active
                              fillColor={colors.skyBlue}
                              chartHeight={plotHeight}
                              chartWidth={lineWidth}
                              hoverSections={hoverSections}
                              hoverSectionColor={colors.hoverBlue}
                              loading={false}
                              data={salary.points}
                              initialized
                              minX={salary.xRangeMin}
                              minY={salary.yRangeMin}
                              maxX={salary.xRangeMax}
                              maxY={salary.yRangeMax}
                            />
                          }
                          initialized
                          index={index}
                          orientation="horizontal"
                          bucketCount={ridgeLineChart.data.length}
                          offset={
                            ((salary.points[0][0] - xRangeMin) /
                              (xRangeMax - xRangeMin)) *
                            100
                          }
                          height="100%"
                          width={`${(salary.count / chartRange) * 100}%`}
                          tooltip={
                            <TooltipContainer>
                              <BodyMicro opacity={0.8}>
                                {i18n('Expected Salary')}
                              </BodyMicro>
                              <H4 mb={0} mt={0}>{`${formatSalary(
                                salary.sectionLimits.middle68LowerBound,
                                locale,
                              )}-${formatSalary(
                                salary.sectionLimits.middle68UpperBound,
                                locale,
                              )}`}</H4>
                            </TooltipContainer>
                          }
                          showTooltip
                          tooltipColor={colors.brandBlue}
                          tooltipOffset={
                            (salary.salary /
                              (salary.xRangeMin + salary.xRangeMax)) *
                            100
                          }
                        />
                      </BarContainer>
                    );
                  }

                  return (
                    <BarContainer
                      key={index * ridgeLineChart.data.length}
                      $plotHeight={plotHeight}
                    />
                  );
                })
              : null}
          </ChartDataWrapper>
        </ChartDataContainer>
        <HorizontalAxisContainer>
          <Axis
            alignToGrid
            direction="x"
            gridLines={xGridLines - 1}
            chartWidth={chartWidth}
            ticks={xAxisTicks.map((t) => ({ name: t, component: t }))}
            type="range"
          />
        </HorizontalAxisContainer>
      </CareerPathChartWrapper>
    </CareerPathChartContainer>
  );
}
